import {
  MetricRecord,
  File,
  ReportingPeriodGroup,
  EntityLabel,
  Scope,
  User,
  Country,
  Region,
  Division,
  Subdivision,
  Site,
  EmissionFactor,
  ReportingPeriod,
  ExternalCompany
} from '@esg/esg-global-types';
import {
  BaseMutationOptions,
  MetricRecordFilters,
  GetMetricRecordsRequest,
  parseResolverResponse,
  CreateMetricRecordRequest,
  CreateBulkMetricRecordRequest,
  SoftDeleteMetricRecordRequest,
  UpdateMetricRecordRequest,
  HardDeleteBulkMetricRecordRequest,
  ResolverQueryRequestMetadata,
  ResolverMutationRequestMetadata,
  CreateMetricRecordClientData,
  UpdateMetricRecordClientData,
  DeleteMetricRecordClientData
} from '@esg/syntiro-api-core';
import { MetadataError } from '@ep/error-handling';
import axios from 'axios';
import { MetricExtended } from '../../lib/metric_capture/metric';
import {
  deleteFileForMetricRecord,
  getDuplicateFileForMetricRecord
} from '../../lib/metric_capture/file';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { apollo_client_v2 } from '../../App';
import { auth } from '../../lib/google/firebase';
import { DocumentNode } from 'graphql';
import { API_ENDPOINT } from '../../env';
import {
  getUserCountries,
  getUserDivisions,
  getUserExternalCompanyList,
  getUserMetrics,
  getUserRegions,
  getUserSites,
  getUserSubdivisions
} from '../../util/user_access';
import { getReportingPeriodGroups } from '../../lib/metric_capture/reporting_period_group';
import { getEmissionFactorsForMetrics } from '../../lib/app/emission_factor';
import { getReportingPeriods } from '../../lib/metric_capture/reporting_period';
import { ActivityOwner } from '../../components/shared/input/select/ActivityOwnerSelect';
import { SiteExtended } from '../../lib/metric_capture/site';
import { FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';
import { GET_METRIC_RECORDS_FULL } from '../queries/metric_record.queries';
import {
  CREATE_BULK_METRIC_RECORD,
  CREATE_METRIC_RECORD,
  HARD_DELETE_BULK_METRIC_RECORD,
  SOFT_DELETE_METRIC_RECORD,
  UPDATE_METRIC_RECORD
} from '../mutations/metric_record.mutations';
import { FetchResult } from '@apollo/client';
import { validateParams } from '../validators/base.validator';
import { isValidCompanyId } from '../validators/company.validator';
import { isValidGroupId } from '../validators/group.validator';
import { getAuthHeaderForUser, handleAuthError } from '../../lib/auth/auth.util';

const SOURCE = 'lib/metric_capture/metric_record.ts';

// Utility type definitions.

export interface MetricRecordData extends Omit<MetricRecord, 'scope'> {
  scope?: Scope | null;
}

export interface MetricRecordWithMetricExtended extends Omit<MetricRecord, 'scope' | 'metric'> {
  metric: MetricExtended;
  scope?: Scope | null;
}

/**
 * Full entity data for foreign reference field mapping on metric records.
 */
export interface MetricRecordEntitiesMap {
  metrics: Record<string, MetricExtended>;
  emission_factors: Record<string, EmissionFactor>;
  reporting_period_groups: Record<string, ReportingPeriodGroup>;
  reporting_periods: Record<string, ReportingPeriod>;
  countries: Record<string, Country>;
  regions: Record<string, Region>;
  divisions: Record<string, Division>;
  subdivisions: Record<string, Subdivision>;
  sites: Record<string, SiteExtended>;
  external_companies: Record<string, ExternalCompany>;
}

export type MetricRecordReportingEntity = Country | Region | Division | Subdivision | Site;

/**
 * The various heirarchical levels that metric record data may be captured on for a company.
 */
export type MetricRecordDataLevel =
  | 'national'
  | 'regional'
  | 'divisional'
  | 'subdivisional'
  | 'site';

export type MetricRecordDataLevelCollection =
  | 'countries'
  | 'regions'
  | 'divisions'
  | 'subdivisions'
  | 'sites';

export const metric_record_data_levels: Array<MetricRecordDataLevel> = [
  'national',
  'regional',
  'divisional',
  'subdivisional',
  'site'
];

/**
 * Client input data for quantitative metric record mutators.
 */
export interface QuantitativeMetricRecordInputData {
  metric: MetricExtended;
  data_level: MetricRecordDataLevel;
  reporting_entity: MetricRecordReportingEntity | null;
  reporting_period: ReportingPeriod | null;
  value: number;
  emission_factors: Array<EmissionFactor>;
  external_company: ExternalCompany | null;
  source: string;
  metric_record_id?: string;
  note?: string | null;
  proportion?: number;
  average_over_year?: boolean;
  calculated_metrics?: Array<MetricExtended>;
}

/**
 * Client input data for qualitative metric record mutators.
 */
export interface QualitativeMetricRecordInputData {
  metric: MetricExtended;
  reporting_period_group: ReportingPeriodGroup;
  status: boolean;
  value: string;
}

// Singular and plural label for model entity.
export const metric_record_label: EntityLabel = {
  one: 'Metric Record',
  many: 'Metric Records'
};

/**
 * Query all metric record documents for current portal company.
 * @param {ResolverQueryRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {MetricRecordFilters} filters Filter parameters to use for metric records query.
 * @param {DocumentNode | undefined} query Optionally pass in specific query to use for desired metric record fields.
 * @returns {Promise<Array<MetricRecord>>}
 */
export const getMetricRecords = async (
  metadata: ResolverQueryRequestMetadata,
  filters: MetricRecordFilters,
  query: DocumentNode = GET_METRIC_RECORDS_FULL
): Promise<Array<MetricRecord>> => {
  validateParams(
    { metadata },
    {
      'metadata.group_id': {
        validate: isValidGroupId,
        message: 'Invalid group_id specified.'
      },
      'metadata.company_id': {
        validate: isValidCompanyId,
        message: 'Invalid company_id specified.'
      }
    }
  );
  const input: GetMetricRecordsRequest['input'] = {
    metadata: metadata,
    filter: {
      deleted: false,
      ...filters
    }
  };
  return apollo_client_v2
    .query({
      variables: { input },
      query: query,
      fetchPolicy: 'no-cache'
    })
    .then((response: FetchResult) => {
      if (!response.data?.getMetricRecords) {
        throw new Error('No data returned');
      }
      const api_result: Array<MetricRecord> | null | undefined = parseResolverResponse<
        Array<MetricRecord>
      >(response.data.getMetricRecords);
      return api_result ? api_result : [];
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getMetricRecords'),
        { metadata, filters, query },
        tracking_id
      );
    });
};

/**
 * Create metric record in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {CreateMetricRecordClientData} data Required data to populate metric record fields.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<MetricRecord | null | undefined>}
 */
export const createMetricRecord = async (
  metadata: ResolverMutationRequestMetadata,
  data: CreateMetricRecordClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<MetricRecord | null | undefined> => {
  validateParams(
    { metadata, data },
    {
      'metadata.group_id': {
        validate: isValidGroupId,
        message: 'Invalid group_id specified.'
      },
      'metadata.company_id': {
        validate: isValidCompanyId,
        message: 'Invalid company_id specified.'
      },
      'data.metric': {
        validate: (metric) => typeof metric === 'object' && !!metric && 'id' in metric,
        message: 'Invalid metric specified.'
      },
      'data.metric_group': {
        validate: (metric_group) =>
          typeof metric_group === 'object' && !!metric_group && 'id' in metric_group,
        message: 'Invalid metric group specified.'
      },
      'data.standard': {
        validate: (standard) => typeof standard === 'object' && !!standard && 'id' in standard,
        message: 'Invalid standard specified.'
      },
      'data.proportion': {
        validate: (proportion) =>
          !proportion || (typeof proportion === 'number' && proportion >= 0 && proportion <= 1),
        message: 'Invalid proportion specified.'
      }
    }
  );

  const input: CreateMetricRecordRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      metric_record: data
    },
    options: options
  };
  return apollo_client_v2
    .mutate({
      mutation: CREATE_METRIC_RECORD,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.createMetricRecord) {
        throw new Error('No data returned');
      }
      return parseResolverResponse<MetricRecord>(response.data.createMetricRecord);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createBulkMetricRecord'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Create bulk metric records in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {Array<CreateMetricRecordClientData>} data Required data to populate metric record fields.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<Array<string> | null | undefined>}
 */
export const createBulkMetricRecord = async (
  metadata: ResolverMutationRequestMetadata,
  data: Array<CreateMetricRecordClientData>,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<Array<string> | null | undefined> => {
  validateParams(
    { metadata },
    {
      'metadata.group_id': {
        validate: isValidGroupId,
        message: 'Invalid group_id specified.'
      },
      'metadata.company_id': {
        validate: isValidCompanyId,
        message: 'Invalid company_id specified.'
      }
    }
  );

  data.forEach((metric_record: CreateMetricRecordClientData) =>
    validateParams(
      {
        metric: metric_record.metric,
        metric_group: metric_record.metric_group,
        standard: metric_record.standard,
        proportion: metric_record.proportion
      },
      {
        metric: {
          validate: (metric) => typeof metric === 'object' && !!metric && 'id' in metric,
          message: 'Invalid metric specified.'
        },
        metric_group: {
          validate: (metric_group) =>
            typeof metric_group === 'object' && !!metric_group && 'id' in metric_group,
          message: 'Invalid metric group specified.'
        },
        standard: {
          validate: (standard) => typeof standard === 'object' && !!standard && 'id' in standard,
          message: 'Invalid standard specified.'
        },
        proportion: {
          validate: (proportion) =>
            !proportion || (typeof proportion === 'number' && proportion >= 0 && proportion <= 1),
          message: 'Invalid proportion specified.'
        }
      }
    )
  );

  const input: CreateBulkMetricRecordRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      metric_records: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: CREATE_BULK_METRIC_RECORD,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.createBulkMetricRecord) {
        throw new Error('No data returned');
      }
      return parseResolverResponse<Array<string>>(response.data.createBulkMetricRecord);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createBulkMetricRecord'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Update a metric record in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {UpdateMetricRecordClientData} data Required data to populate metric record fields.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<MetricRecord | null | undefined>}
 */
export const updateMetricRecord = async (
  metadata: ResolverMutationRequestMetadata,
  data: UpdateMetricRecordClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<MetricRecord | null | undefined> => {
  validateParams(
    { metadata, data },
    {
      'metadata.group_id': {
        validate: isValidGroupId,
        message: 'Invalid group_id specified.'
      },
      'metadata.company_id': {
        validate: isValidCompanyId,
        message: 'Invalid company_id specified.'
      },
      'data.id': {
        validate: (v) => !!v,
        message: 'Invalid metric record id specified.'
      }
    }
  );

  const input: UpdateMetricRecordRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      metric_record: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: UPDATE_METRIC_RECORD,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.updateMetricRecord) {
        throw new Error('No data returned');
      }
      return parseResolverResponse<MetricRecord>(response.data.updateMetricRecord);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateMetricRecord'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Soft delete metric record in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {DeleteMetricRecordClientData} data Required data to delete a given metric record.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<MetricRecord | null | undefined>}
 */
export const softDeleteMetricRecord = async (
  metadata: ResolverMutationRequestMetadata,
  data: DeleteMetricRecordClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<MetricRecord | null | undefined> => {
  validateParams(
    { metadata, data },
    {
      'metadata.group_id': {
        validate: isValidGroupId,
        message: 'Invalid group_id specified.'
      },
      'metadata.company_id': {
        validate: isValidCompanyId,
        message: 'Invalid company_id specified.'
      },
      'data.id': {
        validate: (v) => !!v,
        message: 'Invalid metric record id specified.'
      }
    }
  );

  const input: SoftDeleteMetricRecordRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      metric_record: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: SOFT_DELETE_METRIC_RECORD,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.softDeleteMetricRecord) {
        throw new Error('No data returned');
      }
      return parseResolverResponse<MetricRecord>(response.data.softDeleteMetricRecord);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'softDeleteMetricRecord'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Hard delete metric records for a company, restricted to specific imports.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {string} import_id ID use to hard delete metric records restricted to a specific import.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<void>}
 */
export const hardDeleteBulkMetricRecord = async (
  metadata: ResolverMutationRequestMetadata,
  import_id: string,
  options?: BaseMutationOptions
): Promise<void> => {
  validateParams(
    { metadata },
    {
      'metadata.group_id': {
        validate: isValidGroupId,
        message: 'Invalid group_id specified.'
      },
      'metadata.company_id': {
        validate: isValidCompanyId,
        message: 'Invalid company_id specified.'
      }
    }
  );

  const input: HardDeleteBulkMetricRecordRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      import_id: import_id
    },
    options
  };
  await apollo_client_v2
    .mutate({
      mutation: HARD_DELETE_BULK_METRIC_RECORD,
      variables: { input }
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'hardDeleteBulkMetricRecord'),
        { metadata, import_id },
        tracking_id
      );
    });
};

/**
 * Function to remove file from metric record and potentially from cloud storage if not used by any other metric records
 * @param {string} group_id Group ID of file to remove
 * @param {string} company_id Company ID of file to remove
 * @param {string} metric_record_id ID of Metric Record to remove file from
 * @param {File} delete_file File to remove
 * @returns {Promise<void>}
 */
export const removeMetricRecordFile = async (
  group_id: string,
  company_id: string,
  metric_record_id: string,
  delete_file: File
): Promise<void> => {
  try {
    await deleteFileForMetricRecord(group_id, company_id, delete_file);
    const remaining_file: File | null = await getDuplicateFileForMetricRecord(
      group_id,
      company_id,
      delete_file.name,
      metric_record_id
    );
    if (!remaining_file) {
      await axios({
        method: 'post',
        url: `${API_ENDPOINT}/remove_file`,
        data: { file_path: delete_file.file_path },
        headers: {
          ...(await getAuthHeaderForUser())
        }
      });
    }
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    log(
      'error',
      new MetadataError(
        err instanceof Error
          ? err.message
          : 'Error: lib/metric_capture/metric_record.ts failed on an unknown error while calling removeMetricRecordSupportingDoc.',
        {
          metric_record_id: metric_record_id,
          delete_file: delete_file
        },
        tracking_id
      )
    );
    handleAuthError(err);
    throw new Error(`Error: removeMetricRecordSupportingDoc: ${JSON.stringify(err)}.`);
  }
};

/**
 * Fetches all reference entities needed for the metric record collection UI in a single
 * parallel batch and returns them as ID-keyed maps.
 * @param groupId Group ID of the selected company.
 * @param companyId Company ID.
 * @param user Authenticated user, used to scope access-controlled entity lists.
 */
export async function getMetricRecordEntitiesMap(
  groupId: string,
  companyId: string,
  user: User
): Promise<MetricRecordEntitiesMap> {
  const [
    countries,
    regions,
    divisions,
    subdivisions,
    sites,
    metrics,
    reportingPeriodGroups,
    reportingPeriods,
    externalCompanies
  ]: [
    Country[],
    Region[],
    Division[],
    Subdivision[],
    SiteExtended[],
    MetricExtended[],
    ReportingPeriodGroup[],
    ReportingPeriod[],
    ExternalCompany[]
  ] = await Promise.all([
    getUserCountries(user, groupId, companyId),
    getUserRegions(user, groupId, companyId),
    getUserDivisions(user, groupId, companyId),
    getUserSubdivisions(user, groupId, companyId),
    getUserSites(user, groupId, companyId),
    getUserMetrics(user, groupId, companyId),
    getReportingPeriodGroups(groupId, companyId),
    getReportingPeriods(groupId, companyId),
    getUserExternalCompanyList(user, groupId, companyId)
  ]);

  const emissionFactors: EmissionFactor[] = await getEmissionFactorsForMetrics(
    { group_id: groupId, company_id: companyId },
    metrics
  );

  const generateEntityMap = <MapEntity extends { id: string }>(
    entities: MapEntity[]
  ): Record<string, MapEntity> => {
    const entityMap: Record<string, MapEntity> = {};
    for (const entity of entities) {
      entityMap[entity.id] = entity;
    }
    return entityMap;
  };

  return {
    countries: generateEntityMap(countries),
    regions: generateEntityMap(regions),
    divisions: generateEntityMap(divisions),
    subdivisions: generateEntityMap(subdivisions),
    sites: generateEntityMap(sites),
    reporting_period_groups: generateEntityMap(reportingPeriodGroups),
    metrics: generateEntityMap(metrics),
    emission_factors: generateEntityMap(emissionFactors),
    reporting_periods: generateEntityMap(reportingPeriods),
    external_companies: generateEntityMap(externalCompanies)
  };
}

/**
 * Validate a potential quantitative value against metric record business logic.
 * @param {string | number | null | undefined} value Value to validate.
 * @returns {boolean}
 */
export const validQuantitativeMetricRecordValue = (
  value: string | number | null | undefined
): boolean => {
  const numeric_value = Number(value);
  return Boolean(
    value !== null &&
    value !== undefined &&
    value !== '' &&
    !isNaN(numeric_value) &&
    numeric_value >= 0
  );
};

/**
 * Validate a potential client allocation proportion against metric record business logic.
 * @param {string | number | null | undefined} proportion Proportion to validate.
 * @param {ExtendButton | ActivityOwner | null} activity_owner Internal or External company associated with metric record proportion to validate.
 * @returns {boolean}
 */
export const validQuantitativeMetricRecordProportion = (
  proportion: string | number | null | undefined,
  activity_owner: ExternalCompany | ActivityOwner | null
): boolean => {
  const numeric_proportion = Number(proportion);
  return Boolean(
    proportion !== null &&
    proportion !== undefined &&
    !isNaN(numeric_proportion) &&
    (numeric_proportion === 100 || activity_owner) &&
    numeric_proportion >= 0 &&
    numeric_proportion <= 100
  );
};
